/**
 * @hostingportal/schemas/ids — branded ID types and the branded-ID factory.
 *
 * Every primary key and foreign key in hosting.com CRM is a TEXT id shaped
 * `<prefix>_<uuid-v4>` (NEVER a bare UUID): the prefix keeps the value
 * human-recognisable and the brand prevents passing (say) a `ContactId` where a
 * `CompanyId` is required. The exhaustive prefix registry is 00-CONTRACT §5; this
 * file is its single TypeScript home (PLAN-01, CD-1).
 *
 * Two shapes coexist by design:
 *   - `Brand<string, 'CompanyId'>` — the nominal *value* type used in interfaces
 *     and inferred schema types (matches PLAN-01 §4 signatures exactly).
 *   - `CompanyIdSchema` — the runtime Zod parser/validator (regex-checks the
 *     `<prefix>_<uuid>` shape and brands the output).
 *
 * The lift: the `idSchema` / `newId` / UUID machinery is re-domained from
 * `ClayCommerceApp/packages/schemas/src/core/ids.ts`; the CRM prefix set is new.
 */

import { z } from 'zod'

/**
 * Nominal brand over a base type. `Brand<string, 'CompanyId'>` is assignable to
 * `string` but a plain `string` (or another brand) is NOT assignable to it —
 * which is what blocks cross-entity id mix-ups at compile time.
 */
export type Brand<T, B extends string> = T & { readonly __brand: B }

// ── Branded value types (one per object; match PLAN-01 §4 signatures) ─────────

export type WorkspaceId = Brand<string, 'WorkspaceId'> // prefix ws_
export type UserId = Brand<string, 'UserId'> // prefix usr_
export type MemberId = Brand<string, 'MemberId'> // prefix mem_
export type RoleId = Brand<string, 'RoleId'> // prefix role_
export type CompanyId = Brand<string, 'CompanyId'> // prefix cmp_
export type ContactId = Brand<string, 'ContactId'> // prefix con_
export type AddressId = Brand<string, 'AddressId'> // prefix adr_
export type DealId = Brand<string, 'DealId'> // prefix deal_
export type SubscriptionId = Brand<string, 'SubscriptionId'> // prefix sub_ (per-instance contract product a Client holds)
export type QuoteId = Brand<string, 'QuoteId'> // prefix qte_
export type LineItemId = Brand<string, 'LineItemId'> // prefix qli_
export type InvoiceId = Brand<string, 'InvoiceId'> // prefix inv_
export type ProductId = Brand<string, 'ProductId'> // prefix prd_
export type ActivityId = Brand<string, 'ActivityId'> // prefix act_
export type NoteId = Brand<string, 'NoteId'> // prefix note_
export type TaskId = Brand<string, 'TaskId'> // prefix task_
// Two persisted Upmind-backed custom objects (numeric source type ids treated as
// opaque provenance): Product Adoption + Hosting Service. Address is 2-169664955.
export type ProductAdoptionId = Brand<string, 'ProductAdoptionId'> // prefix padp_
export type HostingServiceId = Brand<string, 'HostingServiceId'> // prefix hsvc_
export type CompanyAliasId = Brand<string, 'CompanyAliasId'> // prefix cmpa_ (alias junction; cal_ is reserved for CalendarEvent per 00-CONTRACT §5)
export type ProductRollupId = Brand<string, 'ProductRollupId'> // prefix prol_
export type InventoryId = Brand<string, 'InventoryId'> // prefix ivt_ (install-volume tier)
export type SequenceId = Brand<string, 'SequenceId'> // prefix seq_
export type SequenceStepId = Brand<string, 'SequenceStepId'> // prefix sqst_
export type SequenceEnrollmentId = Brand<string, 'SequenceEnrollmentId'> // prefix sqen_
export type EmailSendId = Brand<string, 'EmailSendId'> // prefix eml_
export type EmailEventId = Brand<string, 'EmailEventId'> // prefix emev_
export type EmailSuppressionId = Brand<string, 'EmailSuppressionId'> // prefix emsp_
export type EsignId = Brand<string, 'EsignId'> // prefix esign_
export type EsignSignerId = Brand<string, 'EsignSignerId'> // prefix esign_ (signer, distinct table)
export type EsignEventId = Brand<string, 'EsignEventId'> // prefix esign_ (event, distinct table)
export type AuditLogId = Brand<string, 'AuditLogId'> // prefix audit_
export type ConfigCommitId = Brand<string, 'ConfigCommitId'> // prefix cfgc_
export type ConfigBranchId = Brand<string, 'ConfigBranchId'> // prefix cfgb_
export type ConfigTagId = Brand<string, 'ConfigTagId'> // prefix cfgt_
export type AgentRunId = Brand<string, 'AgentRunId'> // prefix agent_
export type AgentSkillId = Brand<string, 'AgentSkillId'> // prefix askl_
export type RagDocumentId = Brand<string, 'RagDocumentId'> // prefix ragd_
export type ZoominfoRunId = Brand<string, 'ZoominfoRunId'> // prefix zie_
export type MixpanelEventId = Brand<string, 'MixpanelEventId'> // prefix mxe_
export type AnalyticsEventId = Brand<string, 'AnalyticsEventId'> // prefix evt_
export type EngagementEventId = Brand<string, 'EngagementEventId'> // prefix enge_
export type CommunicationPreferenceId = Brand<string, 'CommunicationPreferenceId'> // prefix pref_
export type CalendarEventId = Brand<string, 'CalendarEventId'> // prefix cal_ (00-CONTRACT §5)
export type VerticalPackId = Brand<string, 'VerticalPackId'> // prefix vpk_
export type DashboardId = Brand<string, 'DashboardId'> // prefix dsh_
export type SavedViewId = Brand<string, 'SavedViewId'> // prefix savd_
export type MigrationId = Brand<string, 'MigrationId'> // prefix mig_
export type IntegrationConnectionId = Brand<string, 'IntegrationConnectionId'> // prefix intg_
export type IntegrationRunId = Brand<string, 'IntegrationRunId'> // prefix intrun_
// Bookings (Calendly-style scheduling): meeting types, availability, booked slots.
export type EventTypeId = Brand<string, 'EventTypeId'> // prefix evtype_ (evt_ is AnalyticsEventId)
export type AvailabilityScheduleId = Brand<string, 'AvailabilityScheduleId'> // prefix avail_
export type BookingId = Brand<string, 'BookingId'> // prefix bkg_
// Workflow (automation) definitions consumed by the workflow engine.
export type WorkflowId = Brand<string, 'WorkflowId'> // prefix wkf_
// Hosting CRM extensions: the Websites object (cPanel site-level enrichment), the
// generic label-bearing record↔record junction, and the client↔product-line holding.
export type WebsiteId = Brand<string, 'WebsiteId'> // prefix web_
export type RecordAssociationId = Brand<string, 'RecordAssociationId'> // prefix rasc_
export type ClientProductHoldingId = Brand<string, 'ClientProductHoldingId'> // prefix cph_
// RBAC v2 (V3 R5): teams + membership rows.
export type TeamId = Brand<string, 'TeamId'> // prefix team_
export type TeamMemberId = Brand<string, 'TeamMemberId'> // prefix tmm_
// V3 platform foundations (R7, R9-R13; migration 0025).
export type PipelineId = Brand<string, 'PipelineId'> // prefix pipe_
export type PipelineStageId = Brand<string, 'PipelineStageId'> // prefix stage_
export type ApprovalId = Brand<string, 'ApprovalId'> // prefix appr_
export type DiscountId = Brand<string, 'DiscountId'> // prefix disc_
export type ApiKeyId = Brand<string, 'ApiKeyId'> // prefix akey_
export type ApiUsageId = Brand<string, 'ApiUsageId'> // prefix ause_
export type ProviderKeyId = Brand<string, 'ProviderKeyId'> // prefix provk_
export type WebhookSubscriptionId = Brand<string, 'WebhookSubscriptionId'> // prefix whsub_
export type WebhookDeliveryId = Brand<string, 'WebhookDeliveryId'> // prefix whdel_
export type ProspectorId = Brand<string, 'ProspectorId'> // prefix prsp_
export type ProspectorResultId = Brand<string, 'ProspectorResultId'> // prefix prspr_

/**
 * The canonical exhaustive prefix registry (00-CONTRACT §5). The key is the
 * branded type name; the value is the on-the-wire prefix (without the trailing
 * underscore). New prefixes are registered here + in 00-CONTRACT §5 by PR.
 */
export const ID_PREFIXES = {
  WorkspaceId: 'ws',
  UserId: 'usr',
  MemberId: 'mem',
  RoleId: 'role',
  CompanyId: 'cmp',
  ContactId: 'con',
  AddressId: 'adr',
  DealId: 'deal',
  SubscriptionId: 'sub',
  QuoteId: 'qte',
  LineItemId: 'qli',
  InvoiceId: 'inv',
  ProductId: 'prd',
  ActivityId: 'act',
  NoteId: 'note',
  TaskId: 'task',
  ProductAdoptionId: 'padp',
  HostingServiceId: 'hsvc',
  CompanyAliasId: 'cmpa',
  ProductRollupId: 'prol',
  InventoryId: 'ivt',
  SequenceId: 'seq',
  SequenceStepId: 'sqst',
  SequenceEnrollmentId: 'sqen',
  EmailSendId: 'eml',
  EmailEventId: 'emev',
  EmailSuppressionId: 'emsp',
  EsignId: 'esign',
  AgentRunId: 'agent',
  AgentSkillId: 'askl',
  RagDocumentId: 'ragd',
  ZoominfoRunId: 'zie',
  MixpanelEventId: 'mxe',
  AnalyticsEventId: 'evt',
  EngagementEventId: 'enge',
  CommunicationPreferenceId: 'pref',
  CalendarEventId: 'cal',
  AuditLogId: 'audit',
  ConfigCommitId: 'cfgc',
  ConfigBranchId: 'cfgb',
  ConfigTagId: 'cfgt',
  VerticalPackId: 'vpk',
  DashboardId: 'dsh',
  SavedViewId: 'savd',
  MigrationId: 'mig',
  IntegrationConnectionId: 'intg',
  IntegrationRunId: 'intrun',
  EventTypeId: 'evtype',
  AvailabilityScheduleId: 'avail',
  BookingId: 'bkg',
  WorkflowId: 'wkf',
  WebsiteId: 'web',
  RecordAssociationId: 'rasc',
  ClientProductHoldingId: 'cph',
  TeamId: 'team',
  TeamMemberId: 'tmm',
  PipelineId: 'pipe',
  PipelineStageId: 'stage',
  ApprovalId: 'appr',
  DiscountId: 'disc',
  ApiKeyId: 'akey',
  ApiUsageId: 'ause',
  ProviderKeyId: 'provk',
  WebhookSubscriptionId: 'whsub',
  WebhookDeliveryId: 'whdel',
  ProspectorId: 'prsp',
  ProspectorResultId: 'prspr',
} as const

export type IdPrefixName = keyof typeof ID_PREFIXES

// ── UUID + factory machinery (re-domained from Clay core/ids.ts) ──────────────

/**
 * Canonical UUID shape: 8-4-4-4-12 lowercase hex. Lenient on the version /
 * variant nibbles so any well-formed UUID validates, while `newId` always mints
 * a proper v4.
 */
const UUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'

/**
 * Build a Zod schema for a branded id with a given prefix. The output is branded
 * with `B` so the inferred type is e.g. `CompanyId` rather than bare `string`.
 */
export function idSchema<B extends string>(prefix: string) {
  const pattern = new RegExp(`^${prefix}_${UUID_PATTERN}$`)
  return z
    .string()
    .regex(pattern, { message: `Expected an id shaped "${prefix}_<uuid>"` })
    .transform((s) => s as Brand<string, B>)
}

/**
 * RFC-4122 v4 UUID, lowercase. Prefers platform crypto (`crypto.randomUUID`),
 * falling back to `getRandomValues` and finally `Math.random` so id minting
 * never throws in any runtime (browser, Node, worker).
 */
function uuidv4(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID()
  }
  const bytes = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80
  let out = ''
  for (let i = 0; i < 16; i++) {
    out += (bytes[i] ?? 0).toString(16).padStart(2, '0')
    if (i === 3 || i === 5 || i === 7 || i === 9) out += '-'
  }
  return out
}

const PREFIXED_ID = new RegExp(`^[a-z][a-z0-9_]*_${UUID_PATTERN}$`)

/**
 * Validate that `raw` is shaped `<prefix>_<uuid>` for the given prefix and brand
 * it. Throws on mismatch. Use when accepting an externally-supplied id string.
 */
export function brandId<B extends string>(prefix: string, raw: string): Brand<string, B> {
  const pattern = new RegExp(`^${prefix}_${UUID_PATTERN}$`)
  if (!pattern.test(raw)) {
    throw new Error(`Invalid id "${raw}": expected shape "${prefix}_<uuid>"`)
  }
  return raw as Brand<string, B>
}

/** Mint a fresh branded id `<prefix>_<uuid-v4>` (lowercase). */
export function newId<B extends string>(prefix: string): Brand<string, B> {
  return `${prefix}_${uuidv4()}` as Brand<string, B>
}

/** True if `raw` is any well-formed `<prefix>_<uuid>` id. */
export function isBrandedId(raw: string): boolean {
  return PREFIXED_ID.test(raw)
}

// ── Concrete Zod id schemas (one per object) ──────────────────────────────────

export const WorkspaceIdSchema = idSchema<'WorkspaceId'>('ws')
// User ids are the one externally-shaped id: Identity Platform signups arrive
// with the provider's raw UID (e.g. "orJHqWS3yEgw0KSBNV2wV9sptG63") and older
// seeds minted "user_<uuid>". Accept any token-shaped id, keep the brand.
export const UserIdSchema = z
  .string()
  .min(6)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/, { message: 'Expected a user id' })
  .transform((s) => s as Brand<string, 'UserId'>)
export const MemberIdSchema = idSchema<'MemberId'>('mem')
export const RoleIdSchema = idSchema<'RoleId'>('role')
export const CompanyIdSchema = idSchema<'CompanyId'>('cmp')
export const ContactIdSchema = idSchema<'ContactId'>('con')
export const AddressIdSchema = idSchema<'AddressId'>('adr')
export const DealIdSchema = idSchema<'DealId'>('deal')
export const SubscriptionIdSchema = idSchema<'SubscriptionId'>('sub')
export const QuoteIdSchema = idSchema<'QuoteId'>('qte')
export const LineItemIdSchema = idSchema<'LineItemId'>('qli')
export const InvoiceIdSchema = idSchema<'InvoiceId'>('inv')
export const ProductIdSchema = idSchema<'ProductId'>('prd')
export const ActivityIdSchema = idSchema<'ActivityId'>('act')
export const NoteIdSchema = idSchema<'NoteId'>('note')
export const TaskIdSchema = idSchema<'TaskId'>('task')
export const ProductAdoptionIdSchema = idSchema<'ProductAdoptionId'>('padp')
export const HostingServiceIdSchema = idSchema<'HostingServiceId'>('hsvc')
export const CompanyAliasIdSchema = idSchema<'CompanyAliasId'>('cmpa')
export const ProductRollupIdSchema = idSchema<'ProductRollupId'>('prol')
export const InventoryIdSchema = idSchema<'InventoryId'>('ivt')
export const SequenceIdSchema = idSchema<'SequenceId'>('seq')
export const SequenceStepIdSchema = idSchema<'SequenceStepId'>('sqst')
export const SequenceEnrollmentIdSchema = idSchema<'SequenceEnrollmentId'>('sqen')
export const EmailSendIdSchema = idSchema<'EmailSendId'>('eml')
export const EmailEventIdSchema = idSchema<'EmailEventId'>('emev')
export const EmailSuppressionIdSchema = idSchema<'EmailSuppressionId'>('emsp')
export const EsignIdSchema = idSchema<'EsignId'>('esign')
export const AgentRunIdSchema = idSchema<'AgentRunId'>('agent')
export const AgentSkillIdSchema = idSchema<'AgentSkillId'>('askl')
export const RagDocumentIdSchema = idSchema<'RagDocumentId'>('ragd')
export const ZoominfoRunIdSchema = idSchema<'ZoominfoRunId'>('zie')
export const MixpanelEventIdSchema = idSchema<'MixpanelEventId'>('mxe')
export const AnalyticsEventIdSchema = idSchema<'AnalyticsEventId'>('evt')
export const EngagementEventIdSchema = idSchema<'EngagementEventId'>('enge')
export const CommunicationPreferenceIdSchema = idSchema<'CommunicationPreferenceId'>('pref')
export const CalendarEventIdSchema = idSchema<'CalendarEventId'>('cal')
export const AuditLogIdSchema = idSchema<'AuditLogId'>('audit')
export const ConfigCommitIdSchema = idSchema<'ConfigCommitId'>('cfgc')
export const ConfigBranchIdSchema = idSchema<'ConfigBranchId'>('cfgb')
export const ConfigTagIdSchema = idSchema<'ConfigTagId'>('cfgt')
export const VerticalPackIdSchema = idSchema<'VerticalPackId'>('vpk')
export const DashboardIdSchema = idSchema<'DashboardId'>('dsh')
export const SavedViewIdSchema = idSchema<'SavedViewId'>('savd')
export const MigrationIdSchema = idSchema<'MigrationId'>('mig')
export const IntegrationConnectionIdSchema = idSchema<'IntegrationConnectionId'>('intg')
export const IntegrationRunIdSchema = idSchema<'IntegrationRunId'>('intrun')
export const EventTypeIdSchema = idSchema<'EventTypeId'>('evtype')
export const AvailabilityScheduleIdSchema = idSchema<'AvailabilityScheduleId'>('avail')
export const BookingIdSchema = idSchema<'BookingId'>('bkg')
export const WebsiteIdSchema = idSchema<'WebsiteId'>('web')
export const RecordAssociationIdSchema = idSchema<'RecordAssociationId'>('rasc')
export const ClientProductHoldingIdSchema = idSchema<'ClientProductHoldingId'>('cph')
export const TeamIdSchema = idSchema<'TeamId'>('team')
export const TeamMemberIdSchema = idSchema<'TeamMemberId'>('tmm')
export const PipelineIdSchema = idSchema<'PipelineId'>('pipe')
export const PipelineStageIdSchema = idSchema<'PipelineStageId'>('stage')
export const ApprovalIdSchema = idSchema<'ApprovalId'>('appr')
export const DiscountIdSchema = idSchema<'DiscountId'>('disc')
export const ApiKeyIdSchema = idSchema<'ApiKeyId'>('akey')
export const ApiUsageIdSchema = idSchema<'ApiUsageId'>('ause')
export const ProviderKeyIdSchema = idSchema<'ProviderKeyId'>('provk')
export const WebhookSubscriptionIdSchema = idSchema<'WebhookSubscriptionId'>('whsub')
export const WebhookDeliveryIdSchema = idSchema<'WebhookDeliveryId'>('whdel')
export const ProspectorIdSchema = idSchema<'ProspectorId'>('prsp')
export const ProspectorResultIdSchema = idSchema<'ProspectorResultId'>('prspr')
